import {Box} from '@mui/material';
import {t} from 'i18next';
import {useCallback, useEffect, useMemo} from 'react';
import {Controller, SubmitHandler, useForm} from 'react-hook-form';
import {UpsertAnnotationRequest} from '../../../../api/types/document.type';
import {isEmpty} from '../../../../utils/formatters';
import ArrModal, {ModalButton} from '../../../common/arr-modal/ArrModal';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import './UpsertAnnotationDialog.scss';
import ArrTextArea from '../../../common/arr-text-area/ArrTextArea';

const ANNOTATION_FIELD_CHAR_LIMIT = 255;
const ANNOTATION_NAME_REGEX = /^(?=.*[A-Za-z0-9])[A-Za-z0-9 ]+$/;

export interface UpsertAnnotationDialogProps {
  showModal: boolean;
  annotationModel?: IAnnotationForm;
  requestLoading?: boolean;
  annotationNameError?: string;
  onSave?: (formData: IAnnotationForm) => void;
  onClose?: () => void;
  isAnnotationEdited?: boolean;
}

export type IAnnotationForm = Pick<
  UpsertAnnotationRequest,
  'annotationName' | 'annotationDescription'
>;

const UpsertAnnotationDialog: React.FC<UpsertAnnotationDialogProps> = ({
  showModal,
  requestLoading,
  annotationNameError = '',
  onSave,
  onClose,
  annotationModel = {annotationName: '', annotationDescription: ''},
  isAnnotationEdited = false,
}: UpsertAnnotationDialogProps) => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: {isDirty, errors, isValid},
  } = useForm<IAnnotationForm>({
    defaultValues: annotationModel,
    reValidateMode: 'onChange',
    mode: 'onChange',
  });

  const values = watch();

  useEffect(() => {
    if (showModal && !isEmpty(annotationNameError)) {
      setError('annotationName', {
        type: 'pattern',
        message: annotationNameError,
      });
    }
  }, [annotationNameError, setError, showModal]);

  useEffect(() => {
    if (showModal) {
      reset(annotationModel);
    }
  }, [showModal, JSON.stringify(annotationModel), reset]);

  const onSubmit: SubmitHandler<IAnnotationForm> = useCallback(
    (data: IAnnotationForm) => {
      onSave?.({
        ...data,
        annotationName: data?.annotationName?.trimStart(),
      });
    },
    [onSave]
  );

  const dialogActionButtons: ModalButton[] = useMemo(
    () => [
      {
        id: 'close-action',
        label: t('common.cancel'),
        onClick: () => onClose?.(),
        variant: 'secondary',
        disabled: requestLoading,
        dataTestId: 'uad-cancel-btn',
      },
      {
        id: 'save-action',
        label: t('common.save'),
        variant: 'primary',
        type: 'submit',
        loading: requestLoading,
        onClick: handleSubmit(onSubmit),
        ariaDisabled: requestLoading || !isValid || !isDirty ? 'true' : 'false',
        dataTestId: 'uad-save-btn',
      },
    ],
    [requestLoading, handleSubmit, onSubmit, isValid, isDirty, onClose]
  );

  return (
    <ArrModal
      showClose
      showModal={showModal}
      disabledClose={requestLoading}
      className={'upsert-annotation-dialog'}
      title={t(
        `annotationLayer.addEditAnnotation.${isAnnotationEdited ? 'editAnnotationLayer' : 'addAnnotationLayer'}`
      )}
      dataTestId="upsert-annotation-dialog-modal"
      buttons={dialogActionButtons}
      onClose={() => onClose?.()}
      autoFocusCloseButton={true}
       disableFocusTitle={true}
    >
      <Box className="upsert-annotation-dialog-container">
        <form
          onSubmit={handleSubmit(onSubmit)}
          data-testid={`upsert-annotation-dialog-form`}
        >
          <Box className="upsert-annotation-container">
            <ArrTextField
              className={'annotation-name-input'}
              dataTestId={'annotationName-input'}
              state={
                isEmpty(errors?.annotationName?.message) ? 'enabled' : 'error'
              }
              label={t('annotationLayer.addEditAnnotation.layerName')}
              value={values?.annotationName}
              disabled={requestLoading}
              fullWidth
              slotProps={{
                input: {
                  readOnly: requestLoading,
                },
                inputLabel: {disableAnimation: true},
                htmlInput: {
                  maxLength: ANNOTATION_FIELD_CHAR_LIMIT,
                },
              }}
              supportText={errors.annotationName?.message ?? ''}
              showErrorIcon={!isEmpty(errors?.annotationName?.message)}
              aria-invalid={!isEmpty(errors?.annotationName?.message)}
              {...register('annotationName', {
                required: t(
                  'annotationLayer.addEditAnnotation.errors.nameRequired'
                ),
                pattern: {
                  value: ANNOTATION_NAME_REGEX,
                  message: t(
                    'annotationLayer.addEditAnnotation.errors.nameValidation'
                  ),
                },
              })}
            />
          </Box>
          <Box className="annotation-desc-input">
            <Controller
              name="annotationDescription"
              control={control}
              render={({field}) => (
                <ArrTextArea
                  {...field}
                  dataTestId="annotation-description-area"
                  label={t(
                    'annotationLayer.addEditAnnotation.layerDescription'
                  )}
                  value={values?.annotationDescription}
                  aria-label={t(
                    'annotationLayer.addEditAnnotation.layerDescription'
                  )}
                  maxRows={5}
                  rows={4}
                  disabled={requestLoading}
                  maxLength={ANNOTATION_FIELD_CHAR_LIMIT}
                  onChange={(e) => field.onChange(e)}
                  resizable={true}
                />
              )}
            ></Controller>
          </Box>
        </form>
      </Box>
    </ArrModal>
  );
};

export default UpsertAnnotationDialog;
